import {
    PackageStateToTextMapping,
    PackageReasonToTextMapping,
    PackageStateToTextMappingForXPT,
    PackageStateToGenericTextMapping
} from "./dataStore/PackageResultsData";
import {
    AT_STATION,
    EXCHANGE_TYPE,
    DELIVERY,
    INDUCT,
    MANIFEST,
    MFN_PICKUP,
    PENDING_PICKUP,
    PENDING_RETURN_PICKUP,
    RETURN_PICKUP,
    DASH_DELIMITER,
    UNKNOWN
} from "./constants/CommonConstants";
import {isINRegion} from "../util/CommonUtils";
import {getFeaturesFromParent, getAllNodeCapabilitiesFromParent} from "../handler/ParentDataHandler";

export function validateEmail(email) {
    const re = /[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}/igm;
    return re.test(email);
}

export function validateWorkcellId(workcellId) {
    const re = /^[\w]+_(ASL_LINE_\d{1,2}|ADTA_FINGER_[\w]{1,2})$/g;
    return re.test(workcellId);
}

export const chainWalk = (func, defaultValue) => {
    try {
        const value = func()
        return (value === null || value === undefined) ? defaultValue : value;
    } catch (e) {
        return defaultValue
    }
}

export function oldTimeZoneMapper(timezone) {
    if (!timezone) {
        return timezone;
    }
    switch(timezone.trim()) {
        case "US/Eastern":
            return "America/New_York";
        case "US/Central":
            return "America/Chicago";
        case "US/Pacific":
            return "America/Los_Angeles";
        case "US/Mountain":
            return "America/Denver";
        case "US/Arizona":
            return "America/Phoenix";
        case "IST":
            return "Asia/Kolkata";
        default:
            return timezone.trim();
    }
}

export function destinationTypeMapper(destination) {
    if (!destination) {
        return destination;
    }
    if (destination.trim().startsWith("MIDDLE_MILE_NODE") 
        || destination.trim().startsWith("LAST_MILE_NODE") 
        || destination.trim().startsWith("CUSTOMER_ADDRESS")) {
        return destination.toLowerCase().replace(/_/g," ").replace(/(?:^|\s|["'([{])+\S/g, match => match.toUpperCase());
    } else {
        return destination.trim();
    }
}

export function createDimensionString(value, unit) {
    if (value && unit) {
        return value + " " + unit;
    }

    return '';
}

export function createRouteSortCode(vehicleStopNumber, taskStopNumber) {
    if (vehicleStopNumber && taskStopNumber) {
        return vehicleStopNumber + "-(" + taskStopNumber + ")";
    }

    return '';
}

export function handleBlankValue(str) {
    return str ? str : '-';
}
export function convertToBoolean(value) {
    return value ? true : false;
}

/**
 * Extracts and determines the exchange type from a given exchange identifier.
 * @param {String} exchangeID - The exchange identifier to process
 *                 Format: PREFIX-NUMBERS
 *                 Examples:
 *                 - "BB-404-3808945-8483556-46864817723602" (BuyBack exchange)
 *                 - "H2H-effd1860-2bfa-4235-9089-75e2c4ec3813" (Hand-to-Hand exchange)
 *
 * @returns {String} The exchange type corresponding to the prefix,
 *                   returns empty string if exchangeID is null/undefined,
 *                   returns UNKNOWN if prefix is not recognized,
 *
 * @throws {Error} Logs error to console if string processing fails
 *
 * @example
 * // Returns exchange type as BuyBack
 * getExchangeType("BB-404-3808945-8483556-46864817723602");
 *
 * // Returns exchange type as H2H
 * getExchangeType("H2H-effd1860-2bfa-4235-9089-75e2c4ec3813");
 */
export function extractExchangeType(exchangeID) {
    if (!exchangeID) return "";

    const exchangeIDPrefix = exchangeID.split(DASH_DELIMITER)[0];
    return EXCHANGE_TYPE[exchangeIDPrefix] || UNKNOWN;
}

/**
 * The Exchange field in the SCC portal only displays boolean values by default.
 * To enhance BuyBack visibility in the Portal for the IN region,
 * Determines the exchange type based on the provided exchange ID and Region.
 *
 * @param {string|number} exchangeID - The identifier for the exchange
 * @returns {string} The exchange type as a string. If INRegion is true,
 *                   returns the extracted exchange type; otherwise returns the boolean
 *                   conversion of exchangeID as a string
 *
 * @example
 * // For IN Region
 * getExchangeType("BB-404-3808945-8483556-46864817723602", true);
 * // Returns ExchangeType as BuyBack
 *
 * // For Non-IN Region
 * getExchangeType("BB-404-3808945-8483556-46864817723602", false);
 * // Returns ExchangeType as true (boolean string)
 * BackLog SIM to CTI : Amazon.in/Delivery/BuyBackProgram For Adding Unit Tests : https://sim.amazon.com/issues/IN-DEX-BuyBack-9378
 */

export function getExchangeType(exchangeID) {
    return isINRegion() ? extractExchangeType(exchangeID) : convertToBoolean(exchangeID).toString() ;
}

export function handleBlankValueForScheduledDeliveryEnd(str) {
    if (str) {
      return ' - ' + str;
    } else {
      return '';
    }
}

export const getNodeCapabilityList = async () => {
    const capabilityResponse = await getAllNodeCapabilitiesFromParent()
    const capabilityList = chainWalk(() => capabilityResponse.capability, null)
    return JSON.parse(capabilityList);
}

export const getFeatureStatus = async (featureName) => {
    const featureMap = await getFeaturesFromParent([featureName])
    return chainWalk(() => featureMap.data[featureName], false)
}

export function handlePackageStateMapping(page, section, subsection, viewType) {
    if(viewType === "XPT_VIEW") {
        if (PackageStateToTextMappingForXPT[page]
            && PackageStateToTextMappingForXPT[page][section]
            && PackageStateToTextMappingForXPT[page][section][subsection]) {
            return PackageStateToTextMappingForXPT[page][section][subsection];
        }
    }
    if (PackageStateToTextMapping[page]
        && PackageStateToTextMapping[page][section]
        && PackageStateToTextMapping[page][section][subsection]) {
        return PackageStateToTextMapping[page][section][subsection];
    }
    return subsection;
}

export function handlePackageStateToGenericTextMapping(page, section, subsection, viewType, isRtsAgeingGenericLabelsEnabledForReverse) {
    if(isRtsAgeingGenericLabelsEnabledForReverse){
        return chainWalk(() => PackageStateToGenericTextMapping[page][section][subsection], handlePackageStateMapping(page, section, subsection, viewType))
    }
    return handlePackageStateMapping(page, section, subsection, viewType);
}


export function handlePackageReasonMapping(page, section, subsection) {
    if (PackageReasonToTextMapping[page]
        && PackageReasonToTextMapping[page][section]
        && PackageReasonToTextMapping[page][section][subsection]) {
        return PackageReasonToTextMapping[page][section][subsection];
    }
    return subsection;
}

export function convertLocaleTimeToFormat(dateTimeStr) {
    if (!dateTimeStr) {
        return "-";
    }
    let dateTimeString = dateTimeStr.split(" ");
    let dateStr = dateTimeString[0].replace(',', '');
    let timeStr = dateTimeString[1];
    let amPm = dateTimeString[2];

    let month = dateStr.split('/')[0];
    let date = dateStr.split('/')[1];
    let year = dateStr.split('/')[2];

    dateStr = year + "-" + month + "-" + date;
    let hour = timeStr.split(':')[0];
    let minutes = timeStr.split(':')[1];
    let seconds = timeStr.split(':')[2];
    // If PM add 12 to convert to 24 hours format.
    if (amPm === "PM") {
        hour = parseInt(hour, 10);
        if (hour !== 12) {
            hour = hour + 12;
        }
    } else if (parseInt(hour, 10) < 10 && hour.length === 1) {
        // For AM hour < 10, append a 0, For example: 01.
        hour = "0" + hour;
    } else if (parseInt(hour, 10) === 12) {
        // If 12 AM then make it 00 hours.
        hour = "00";
    }


    timeStr = hour + ":" + minutes + ":" + seconds;

    return dateStr + " " + timeStr;
}

/**
 * Converts invalid package state to valid PSS package state
 */
export function convertToValidPackageState (packageList) {
     if(isINRegion()) {
         packageList.forEach(packageData => {
             /**
              Fix to mark AT_STATION packages to map to correct PSS state
              */
             if (packageData.state === AT_STATION ) {
                 switch (packageData.packageType) {
                     case MFN_PICKUP:
                         packageData.state = PENDING_PICKUP;
                         break;
                     case RETURN_PICKUP:
                         packageData.state = PENDING_RETURN_PICKUP;
                         break;
                     case DELIVERY:
                         packageData.state = INDUCT;
                         break;
                     default:
                         packageData.state = MANIFEST;
                 }
             }
         });
     }

     return packageList;
}


// WEBPACK FOOTER //
// ./src/commons/CommonUtils.js